import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Import, ChevronDown } from 'lucide-react';
import { Dropdown } from 'components/molecules/dropdown';
import { Card } from 'components/atoms/Card';
import { Box, Flex } from 'rebass/styled-components';
import pluralize from 'pluralize';
import { useSourceDetails } from '../hooks/useSourceDetails';

export const SourceDetails = memo(({ sources, trigger = null, placement = 'bottom-start' }) => {
  const sourcesToRender = useSourceDetails(sources);
  if (sourcesToRender.length === 0) {
    return null;
  }

  return (
    <Box
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <Dropdown
        placement={placement}
        trigger={
          trigger ?? (
            <Flex
              onClick={(event) => event.stopPropagation()}
              sx={{
                cursor: 'pointer',
                gap: '8px',
                color: 'text.subtle',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: '400',
                lineHeight: '16px',

                svg: {
                  color: 'text.subtle',
                },
              }}
            >
              <Box as={Import} size={16} />
              <Box>
                {sourcesToRender.length} {pluralize('Source', sourcesToRender.length)}
              </Box>
              <Box as={ChevronDown} size={16} />
            </Flex>
          )
        }
      >
        <Card
          width="fit-content"
          p="16px"
          minWidth="200px"
          sx={{ gap: '8px', display: 'flex', flexDirection: 'column' }}
        >
          {sourcesToRender.map((source) => (
            <Flex
              sx={{
                gap: '16px',
                alignItems: 'center',
                overflow: 'hidden',
                color: 'text.main',
                textOverflow: 'ellipsis',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: '400',
                lineHeight: '140%',
                justifyContent: 'space-between',
              }}
              key={source.id}
            >
              <Flex sx={{ gap: '8px', alignItems: 'center' }}>
                <Box sx={{ color: 'neutral.400' }} size={16} as={source.icon} />
                <Box>{source.title}</Box>
              </Flex>
              {source.addedOn && (
                <Box color="text.subtle">
                  {new Date(source.addedOn).toLocaleDateString('en-US', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </Box>
              )}
            </Flex>
          ))}
        </Card>
      </Dropdown>
    </Box>
  );
});

SourceDetails.propTypes = {
  sources: PropTypes.array,
  trigger: PropTypes.node,
  placement: PropTypes.string,
};

SourceDetails.defaultProps = {
  sources: [],
  trigger: null,
  placement: 'bottom-start',
};
